// slice.ts
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import { createSlice } from '@reduxjs/toolkit'
import { loginUser, logoutCmcUser, validateSession } from './thunks'
import { LOGOUT } from '../globalActions'

export interface UserState {
  /** Whether or not the current user is logged in */
  isLoggedIn: boolean;
  /** Whether or not the current user is an admin */
  isAdmin: boolean;
  /** The current sessionId of the logged in user */
  sessionId: string | null;
  /** The userId of the logged in user */
  userId: string | null;
  /** The real name of the user */
  userRealName: string | null;
  /** The data center this user is logged into */
  dataCenter: string | null;
  /** The current state of a login request */
  loginRequestState: 'idle' | 'pending' | 'fulfilled' | 'rejected';
}

const initialState: UserState = {
  isLoggedIn: false,
  isAdmin: false,
  sessionId: null,
  userId: null,
  userRealName: null,
  dataCenter: null,
  loginRequestState: 'idle'
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLoginRequestState: (state, action) => {
      state.loginRequestState = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loginUser.pending, (state) => {
      state.loginRequestState = 'pending'
    })

    builder.addCase(loginUser.rejected, (state) => {
      state.loginRequestState = 'rejected'
    })

    builder.addCase(loginUser.fulfilled, (state, action) => {
      const { payload } = action
      state.isLoggedIn = true
      state.userId = payload.UserId
      state.sessionId = payload.SessionGuid
      state.userRealName = payload.RealName
      state.dataCenter = payload.dataCenter
      state.isAdmin = true // TODO: Will need to implement roles later
      state.loginRequestState = 'fulfilled'
    })

    builder.addCase(validateSession.fulfilled, (state, action) => {
      state.isLoggedIn = state.isLoggedIn && action.payload
    })

    builder.addCase(validateSession.rejected, (state) => {
      state.isLoggedIn = false
    })

    builder.addCase(logoutCmcUser.pending, (state) => {
      state.isLoggedIn = false
    })

    // Reset state on logout
    builder.addCase(LOGOUT, () => initialState)
  }
})

export default userSlice.reducer
export const {
  setLoginRequestState
} = userSlice.actions
